import * as React from "react";
import { useSearchParams } from "react-router";

/**
 * A request, carried in the URL, for the home screen to lead with one particular thing.
 *
 * Club-in-Club's onboarding ends on "Book my assessment", and its portal cannot navigate
 * inside our app — it can only send the browser to a URL. That URL is the home screen
 * with `?recommend=assessment`; the home screen reads it and puts the DUPR assessment
 * front and centre. A query parameter rather than a dedicated route so that the member
 * still lands on the whole home screen, with their fresh points in view.
 */

export const RECOMMEND_PARAM = "recommend";

export type HomeRecommendation = "assessment";

function parse(value: string | null): HomeRecommendation | null {
    return value === "assessment" ? value : null;
}

/**
 * The recommendation the URL arrived with, or null.
 *
 * Captured once on mount and then stripped from the URL, so a reload or a share of the
 * address does not carry the nudge along, while the value survives for the life of the
 * screen. Stripped through the router rather than `history.replaceState`: the app runs
 * under `createBrowserRouter`, whose location would otherwise fall out of step with the
 * address bar.
 */
export default function useHomeRecommendation(): HomeRecommendation | null {
    const [searchParams, setSearchParams] = useSearchParams();
    const [value] = React.useState(() =>
        parse(searchParams.get(RECOMMEND_PARAM)),
    );

    // Strips the parameter once it has been read. Honest dependencies, no loop: after
    // the replace, `searchParams` no longer carries the key and the effect is a no-op.
    React.useEffect(() => {
        if (!searchParams.has(RECOMMEND_PARAM)) return;
        const next = new URLSearchParams(searchParams);
        next.delete(RECOMMEND_PARAM);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    return value;
}
